/**
 * Types for GET /api/contract/{id}/details.
 *
 * Mirrors the backend `com.kft.dagri.dto.Loan.ContractDetailDto` family after
 * the multi-bank rollout:
 *
 *  - A contract has ONE financing bank (`financingBank`, nullable for legacy rows).
 *  - KYC / Qena linkage is PER account (`farmerBankAccount`, nullable for legacy rows),
 *    not per customer. The same farmer can have one account per bank, each with
 *    its own lifecycle.
 *  - The legacy single-bank fields that used to live on the customer block
 *    (bankAccount, bankVerified, bankCustomerId, bankNarration, ...) no longer
 *    exist. Read banking state from `farmerBankAccount` on the contract detail.
 */

export type ContractKycStatus =
  | "NOT_STARTED"
  | "PENDING"
  | "REGISTERED"
  | "VERIFIED"
  | "DECLINED"
  | "FAILED";

/**
 * Per-entitlement loan lifecycle - mirror of backend
 * `com.kft.dagri.input.controller.LoanStatus`.
 *
 * Each phase has a paired success / failure state so the UI can render distinct
 * status chips and the retry affordance can target exactly one Qena step.
 *
 * Two-step contract approval:
 *   - On a successful loan request at the bank, the line is stamped
 *     {@code CONTRACT_VERIFICATION_PENDING} (awaiting customer contract OTP).
 *   - On successful contract OTP verification, the line is promoted to
 *     {@code APPROVED}.
 *   Legacy rows persisted as {@code APPROVED} from before the OTP flow are
 *   intentionally not backfilled and remain {@code APPROVED}.
 */
export type EntitlementLoanStatus =
  | "PENDING"
  | "REJECTED"
  /** Loan request succeeded at bank; awaiting customer contract OTP verification. */
  | "CONTRACT_VERIFICATION_PENDING"
  /** Customer has confirmed the loan contract via the contract-financing OTP (also used by legacy pre-OTP-flow rows that were never backfilled). */
  | "APPROVED"
  | "LOAN_REQUEST_FAILED"
  | "DISBURSEMENT_ACCOUNT_ADDED"
  | "DISBURSEMENT_ACCOUNT_FAILED"
  | "DISBURSED"
  | "DISBURSEMENT_FAILED";

/** Loan-status values for which the operator can retry the failed phase. */
export const RETRYABLE_LOAN_STATUSES: EntitlementLoanStatus[] = [
  "LOAN_REQUEST_FAILED",
  "DISBURSEMENT_ACCOUNT_FAILED",
  "DISBURSEMENT_FAILED",
];

export const isRetryableLoanStatus = (
  status?: string | null,
): status is EntitlementLoanStatus =>
  !!status &&
  (RETRYABLE_LOAN_STATUSES as string[]).includes(status as string);

/** Operator-facing label for the retry action driven by the current loan status. */
export const retryLabelForStatus = (status?: string | null): string => {
  switch (status) {
    case "LOAN_REQUEST_FAILED":
      return "Retry Loan Request";
    case "DISBURSEMENT_ACCOUNT_FAILED":
      return "Retry Add Disbursement Account";
    case "DISBURSEMENT_FAILED":
      return "Retry Disbursement";
    default:
      return "Retry";
  }
};

export interface FinancingBankSummary {
  id: number;
  bankName: string;
  bankCode?: string | null;
}

export interface ContractFarmerBankAccount {
  id: number;
  accountNumber: string;
  accountName?: string | null;
  accountBranch?: string | null;
  isPrimary?: boolean | null;
  isActive?: boolean | null;

  kycStatus: ContractKycStatus;
  /**
   * Convenience flag: kyc lifecycle reached a terminal success AND Qena has linked
   * the account. Use this to hide the Approve / Reject buttons on the KYF card.
   */
  kycApproved: boolean;
  /** True iff Qena has issued a bankCustomerId for this account in this tenant. */
  qenaLinked: boolean;
  bankCustomerId?: string | null;
  kycNarration?: string | null;
  kycLastError?: string | null;
  lastKycAttemptAt?: string | null;
  verifiedAt?: string | null;
}

export interface ContractDetail {
  contract: {
    id: number;
    contractNumber: string;
    customerId: number;
    fullName?: string | null;
    phoneNumber?: string | null;
    offTaker?: string | null;
    product?: string | null;
    quantity: number;
    contractPrice: number;
    spotPrice: number;
    contractStatus: string;
    farmingStageStatus?: string | null;
    contractDate?: string | null;
    harvestDate?: string | null;
    inputCredit?: number;
    insuranceCredit?: number;
    loanStatus?: string | null;
    /** New with multi-bank: may be null for legacy contracts. */
    financingBankId?: number | null;
    financingBankName?: string | null;
  };
  financingBank: FinancingBankSummary | null;
  farmerBankAccount: ContractFarmerBankAccount | null;
  customerDetail?: unknown;
  entitlements?: unknown[];
  insurance?: unknown;
}
